import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "@/shared/components/Loading/Loading";
import ErrorPage from "@/modules/main/ErrorPage/ErrorPage";
import WelcomeElement from "@/shared/components/candidate/scenes/WelcomeElement/WelcomeElement";
import { convertToFuzzyTimeFormat } from "@/shared/utils/common.utils";
import { getRouteWithSearchParameter } from "@/shared/utils/route/route.utils";
import { Button } from "@/ui-components/commonComponents/Button/Button";
import { CandidateAssessmentViewTypes } from "./CandidateAssessment";
import CandidateAssessmentDesktop from "./CandidateAssessmentDesktop";
import { TextLocations } from "@/modules/assessments/scenes/CreateAssessment/createAssessment.constants";
import { ResolvedLocalizedContent } from "@/modules/assessments/types/test.interface";
import {
  CANDIDATE_ROUTE,
  LOGIN_ROUTE,
  ASSESSMENT_ID_SEARCH,
  ASSESSMENT_CODE_SEARCH,
} from "@/routing/AppRouter/routes.constants";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import CandidateAssessmentMobile from "./CandidateAssessmentMobile";
import { AssessmentHttpService } from "@/modules/assessments/services/assessment-http.service";
import { NO_LONGER_AVAILABLE_EN } from "./candidate-assessment.constants";

type AssessmentPreview = {
  name: ResolvedLocalizedContent;
  client: {
    name: string;
    logoUrl?: string;
  };
  titleImageUrl?: string;
  assessmentTexts: Array<{
    body: ResolvedLocalizedContent;
    location: {
      name: string;
    };
  }>;
  meta: {
    totalTime: number;
  };
};

function useAssessmentPreview({ code, assessmentId }: { code?: string; assessmentId?: string }) {
  const [error, setError] = useState();
  const [assessment, setAssessment] = useState<AssessmentPreview>();

  useEffect(() => {
    if (!code && !assessmentId) {
      return;
    }

    async function getAssessmentPreview() {
      try {
        const { data } = await AssessmentHttpService.getAssessmentPreview({ code, assessmentId });
        setAssessment(data);
      } catch (e: any) {
        setError(e);
      }
    }

    getAssessmentPreview();
  }, [code, assessmentId]);
  return {
    assessment,
    error,
  };
}

export default function PublicCandidateRegistration() {
  const navigate = useNavigate();
  const params = useParams();
  const searchParam = params.assessmentId ? ASSESSMENT_ID_SEARCH : ASSESSMENT_CODE_SEARCH;
  const param = params.assessmentId ?? params.code;
  const { assessment, error } = useAssessmentPreview(params);
  const { xxl, xl, lg } = useBreakpoint();
  const isDesktopView = xxl || xl || lg;

  if (error) {
    return <ErrorPage isSignedIn={false} isClient={false} isAdmin={false} errorReason={NO_LONGER_AVAILABLE_EN} />;
  }

  if (!assessment) {
    return <Loading />;
  }

  const introText = assessment.assessmentTexts.find((text: any) => text.location.name === TextLocations.introText);
  const introDescription = introText?.body?.value ?? undefined;
  const leftTime = convertToFuzzyTimeFormat({ milliseconds: assessment.meta.totalTime });
  const redirectPath = getRouteWithSearchParameter(`/${CANDIDATE_ROUTE}/${LOGIN_ROUTE}`, searchParam, String(param));

  return (
    <>
      {isDesktopView ? (
        <>
          <CandidateAssessmentDesktop
            viewType={CandidateAssessmentViewTypes.start}
            clientLogo={assessment.client.logoUrl}
            clientName={assessment.client.name}
            assessmentImageUrl={assessment.titleImageUrl}
          >
            <WelcomeElement
              type={CandidateAssessmentViewTypes.start}
              title={assessment.name.value ?? undefined}
              description={introDescription}
              leftTime={leftTime}
              footer={
                <Button
                  onClick={() => {
                    navigate(redirectPath);
                  }}
                >
                  Register to Start
                </Button>
              }
            />
          </CandidateAssessmentDesktop>
        </>
      ) : (
        <>
          <CandidateAssessmentMobile
            viewType={CandidateAssessmentViewTypes.start}
            clientLogo={assessment.client.logoUrl}
            clientName={assessment.client.name}
            assessmentImageUrl={assessment.titleImageUrl}
            childrenSubmitButton={
              <Button
                onClick={() => {
                  navigate(redirectPath);
                }}
                fullWidth={true}
              >
                Register to Start
              </Button>
            }
          >
            <WelcomeElement
              type={CandidateAssessmentViewTypes.start}
              title={assessment.name.value ?? undefined}
              description={introDescription}
              leftTime={leftTime}
            />
          </CandidateAssessmentMobile>
        </>
      )}
    </>
  );
}
